import React from "react";
import {
  Activity,
  Ban,
  Bot,
  CheckCircle2,
  Link2,
  Radio,
  ShieldAlert,
  ShieldCheck,
  Wifi,
  WifiOff,
} from "lucide-react";

import { cn } from "../../lib/utils";

export const toneClasses = {
  good: "border-emerald-500/25 bg-emerald-500/10 text-emerald-100",
  warn: "border-amber-500/30 bg-amber-500/10 text-amber-100",
  bad: "border-red-500/30 bg-red-500/10 text-red-100",
  neutral: "border-white/10 bg-white/[0.04] text-white",
};

export const humanize = (value, fallback = "No decision recorded") => {
  const normalized = String(value || "").trim();
  if (!normalized) return fallback;
  return normalized
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());
};

const TruthItem = ({ icon: Icon, label, value, detail, tone = "neutral", className }) => (
  <div className={cn("min-w-0 rounded-md border px-3 py-2", toneClasses[tone], className)}>
    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] opacity-65">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span>{label}</span>
    </div>
    <div className="mt-1 truncate text-sm font-semibold" title={value}>{value}</div>
    {detail ? <div className="mt-0.5 truncate text-[11px] opacity-65" title={detail}>{detail}</div> : null}
  </div>
);

const CompactTruthSummary = ({ entry, protection, latestDecision, marketLive, marketQuality }) => {
  const EntryIcon = entry.icon;
  const ProtectionIcon = protection.icon;
  const decisionValue = humanize(latestDecision?.decision || latestDecision?.status);
  const decisionDetail = humanize(
    latestDecision?.reason_code,
    marketLive ? marketQuality : `Market data: ${marketQuality || "delayed"}`,
  );
  return (
    <section className={cn("overflow-hidden rounded-md border", toneClasses[entry.tone])} aria-label="Trading status">
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <EntryIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-65">New entries</div>
          <div className="mt-0.5 text-sm font-semibold">{entry.value}</div>
          <div className="truncate text-[11px] opacity-65" title={entry.detail}>{entry.detail}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 border-t border-current/10 bg-black/10">
        <div className="min-w-0 border-r border-current/10 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] opacity-60">
            <ProtectionIcon className="h-3 w-3 shrink-0" />
            Protection
          </div>
          <div className="mt-1 truncate text-xs font-medium" title={protection.detail}>{protection.value}</div>
        </div>
        <div className="min-w-0 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] opacity-60">
            <Bot className="h-3 w-3 shrink-0" />
            Last decision
          </div>
          <div className="mt-1 truncate text-xs font-medium" title={decisionDetail}>{decisionValue}</div>
        </div>
      </div>
    </section>
  );
};

export const resolveEntryState = ({
  hasAccount,
  accountActive,
  accountCanTrade,
  brokerConnected,
  brokerStateKnown,
  autoTradeEnabled,
  inAutomationScope,
  hasActiveLink,
}) => {
  if (!hasAccount) return { value: "Account required", detail: "Connect or select a trading account", tone: "warn", icon: Link2 };
  if (!accountActive) return { value: "Entry blocked", detail: "Account is inactive", tone: "bad", icon: Ban };
  if (!accountCanTrade) return { value: "Entry blocked", detail: "Account cannot trade", tone: "bad", icon: Ban };
  if (!brokerConnected) return { value: "Entry blocked", detail: "Broker connection is unavailable", tone: "bad", icon: WifiOff };
  if (!brokerStateKnown) return { value: "Blocked this cycle", detail: "Waiting for confirmed broker state", tone: "warn", icon: ShieldAlert };
  if (!autoTradeEnabled || !inAutomationScope) return { value: "Manual monitoring", detail: "Auto-trade is not enabled for this scope", tone: "neutral", icon: Activity };
  if (!hasActiveLink) return { value: "Setup incomplete", detail: "No active strategy link for this instrument", tone: "warn", icon: Link2 };
  return { value: "Safety checks armed", detail: "Every entry still requires runtime readiness", tone: "good", icon: ShieldCheck };
};

export const resolveProtectionState = ({ hasOpenPosition, unsafePosition, protectionConfirmed, protectionPending }) => {
  if (!hasOpenPosition) return { value: "Broker flat", detail: "No open position for this instrument", tone: "neutral", icon: CheckCircle2 };
  if (unsafePosition) return { value: "Position unsafe", detail: "Protection needs immediate attention", tone: "bad", icon: ShieldAlert };
  if (protectionConfirmed) return { value: "Stop confirmed", detail: "Broker protection is confirmed", tone: "good", icon: ShieldCheck };
  if (protectionPending) return { value: "Protection pending", detail: "Waiting for broker confirmation", tone: "warn", icon: Radio };
  return { value: "Protection unknown", detail: "Do not assume an active broker stop", tone: "bad", icon: ShieldAlert };
};

const TradingTruthBar = ({
  accountName,
  accountActive = true,
  accountCanTrade = true,
  brokerConnected,
  brokerStateKnown,
  symbol,
  contractLabel,
  strategyName,
  strategyTimeframe,
  chartTimeframe,
  marketLive,
  marketQuality,
  autoTradeEnabled,
  inAutomationScope,
  hasActiveLink,
  hasOpenPosition,
  unsafePosition,
  protectionConfirmed,
  protectionPending,
  latestDecision,
  compact = false,
  className,
}) => {
  const entry = resolveEntryState({
    hasAccount: Boolean(accountName),
    accountActive,
    accountCanTrade,
    brokerConnected,
    brokerStateKnown,
    autoTradeEnabled,
    inAutomationScope,
    hasActiveLink,
  });
  const protection = resolveProtectionState({
    hasOpenPosition,
    unsafePosition,
    protectionConfirmed,
    protectionPending,
  });
  const strategyTime = Number(strategyTimeframe) > 0 ? `${strategyTimeframe}m` : "Not linked";
  const chartTime = chartTimeframe || "Unknown";
  const timeframesMatch = Number(strategyTimeframe) > 0 && String(chartTime).toLowerCase() === `${strategyTimeframe}m`;

  if (compact) {
    return (
      <CompactTruthSummary
        entry={entry}
        protection={protection}
        latestDecision={latestDecision}
        marketLive={marketLive}
        marketQuality={marketQuality}
      />
    );
  }

  return (
    <section
      className={cn("grid gap-2 lg:grid-cols-5", className)}
      aria-label="Trading status"
    >
      <TruthItem
        icon={brokerConnected && brokerStateKnown ? Wifi : WifiOff}
        label="Account and broker"
        value={accountName || "No account selected"}
        detail={brokerConnected ? (brokerStateKnown ? "Connected - broker state confirmed" : "Connected - broker state unknown") : "Broker unavailable"}
        tone={brokerConnected && brokerStateKnown ? "good" : accountName ? "bad" : "warn"}
      />
      <TruthItem
        icon={Activity}
        label="Instrument and strategy"
        value={`${contractLabel || symbol || "No instrument"} - ${strategyName || "No active strategy"}`}
        detail={`Signal ${strategyTime} - chart ${chartTime}${timeframesMatch ? "" : " (different views)"}`}
        tone={hasActiveLink ? "neutral" : "warn"}
      />
      <TruthItem
        icon={entry.icon}
        label="New entries"
        value={entry.value}
        detail={entry.detail}
        tone={entry.tone}
      />
      <TruthItem
        icon={protection.icon}
        label="Position protection"
        value={protection.value}
        detail={protection.detail}
        tone={protection.tone}
      />
      <TruthItem
        icon={Bot}
        label="Last automation decision"
        value={humanize(latestDecision?.decision || latestDecision?.status)}
        detail={humanize(latestDecision?.reason_code, marketLive ? marketQuality : `Market data: ${marketQuality || "delayed"}`)}
        tone={String(latestDecision?.decision || "").toLowerCase() === "execute" ? "good" : "neutral"}
      />
    </section>
  );
};

export default React.memo(TradingTruthBar);
